import {
  getUniqueViewGUID,
  pushPreviewUniqueView,
  pushStoryInteraction,
  INTERACTION_TYPE,
} from '@/helpers/dataLayer';

const DWELL_MS = 5000;
const INTERACTION_EVENTS = ['click', 'mouseover', 'touchstart', 'scroll'];

export default {
  created() {
    this.previewTracking = {
      firstSlotDone: false,
      firedForFirstSlot: false,
      dwellTimer: null,
      observer: null,
      interactionHandler: null,
      scrollFallbackTarget: null,
      scrollFallbackHandler: null,
      rafPending: false,
      lastAdvanceReason: '',
      currentGUID: '',
    };
  },
  watch: {
    selected(newItem) {
      this.handlePreviewSelectionChanged(newItem);
    },
  },
  beforeUnmount() {
    this.teardownFirstStoryTracking();
  },
  methods: {
    handlePreviewSelectionChanged(newItem) {
      this.teardownFirstStoryTracking();
      if (!newItem) return;
      if (!this.previewTracking.firstSlotDone) {
        this.previewTracking.firstSlotDone = true;
        this.$nextTick(() => {
          this.setupFirstStoryTracking();
        });
      } else {
        // The shared <tabs> component always
        // auto-emits an initial tabChange created() even when the resolved tab is already current
        const reason = this.previewTracking.lastAdvanceReason;
        this.previewTracking.lastAdvanceReason = '';
        if (!reason) return;
        this.$nextTick(() => {
          this.firePreviewUniqueView();
        });
      }
    },
    setupFirstStoryTracking() {
      const activeStoryEl = this.getActiveStoryElement();
      if (!activeStoryEl) return;
      const state = this.previewTracking;
      state.firedForFirstSlot = false;

      const fire = () => {
        if (state.firedForFirstSlot) return;
        state.firedForFirstSlot = true;
        this.teardownFirstStoryTracking();
        this.firePreviewUniqueView();
      };

      const onInteraction = () => fire();
      state.interactionHandler = onInteraction;
      INTERACTION_EVENTS.forEach((eventName) => {
        this.$el.addEventListener(eventName, onInteraction, { capture: true, passive: true });
      });

      if ('IntersectionObserver' in window) {
        state.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!state.dwellTimer) {
                  state.dwellTimer = setTimeout(fire, DWELL_MS);
                }
              } else if (state.dwellTimer) {
                clearTimeout(state.dwellTimer);
                state.dwellTimer = null;
              }
            });
          },
          { root: null, threshold: 0 },
        );
        state.observer.observe(activeStoryEl);
      } else {
        state.scrollFallbackTarget = activeStoryEl;
        state.scrollFallbackHandler = () => {
          if (state.rafPending) return;
          state.rafPending = true;
          window.requestAnimationFrame(() => {
            state.rafPending = false;
            if (!state.scrollFallbackTarget) return;
            const rect = state.scrollFallbackTarget.getBoundingClientRect();
            const isVisible =
              rect.top < window.innerHeight &&
              rect.bottom > 0 &&
              rect.left < window.innerWidth &&
              rect.right > 0;
            if (isVisible) {
              if (!state.dwellTimer) {
                state.dwellTimer = setTimeout(fire, DWELL_MS);
              }
            } else if (state.dwellTimer) {
              clearTimeout(state.dwellTimer);
              state.dwellTimer = null;
            }
          });
        };
        window.addEventListener('scroll', state.scrollFallbackHandler, { passive: true });
        window.addEventListener('resize', state.scrollFallbackHandler);
        state.scrollFallbackHandler();
      }
    },
    teardownFirstStoryTracking() {
      const state = this.previewTracking;
      if (state.dwellTimer) {
        clearTimeout(state.dwellTimer);
        state.dwellTimer = null;
      }
      if (state.observer) {
        state.observer.disconnect();
        state.observer = null;
      }
      if (state.interactionHandler) {
        INTERACTION_EVENTS.forEach((eventName) => {
          this.$el.removeEventListener(eventName, state.interactionHandler, { capture: true });
        });
        state.interactionHandler = null;
      }
      if (state.scrollFallbackHandler) {
        window.removeEventListener('scroll', state.scrollFallbackHandler);
        window.removeEventListener('resize', state.scrollFallbackHandler);
        state.scrollFallbackHandler = null;
      }
      state.scrollFallbackTarget = null;
    },
    getActiveStoryTrackingContext() {
      const item = this.selected;
      const data = item && this.storyMap[item.index];
      if (!data) return {};
      const trackingIdField = this.isLayoutDesktop
        ? data.cmsDesktopTrackingId
        : data.cmsMobileTrackingId;
      const [storyId, sectionId] = (trackingIdField || '').split('-');
      const sectionType = this.isLayoutDesktop ? 'Desktop' : 'Mobile';
      const activeStoryEl = this.getActiveStoryElement();
      const regionEl = activeStoryEl && activeStoryEl.querySelector('[data-analytics-id]');
      const analyticsIdRaw = (regionEl && regionEl.getAttribute('data-analytics-id')) || '';
      const [, storyVersionId, , regionId, regionTrackingId] = analyticsIdRaw.split(/[-]/);
      return {
        storyId,
        storyVersionId: storyVersionId || 0,
        sectionId,
        sectionType,
        regionId,
        regionTrackingId,
      };
    },
    firePreviewUniqueView() {
      const ctx = this.getActiveStoryTrackingContext();
      if (!ctx.storyId) return;
      const previewUniqueViewGUID = getUniqueViewGUID();
      this.previewTracking.currentGUID = previewUniqueViewGUID;

      pushPreviewUniqueView({
        storyId: ctx.storyId,
        storyVersionId: ctx.storyVersionId,
        sectionId: ctx.sectionId,
        sectionType: ctx.sectionType,
        previewUniqueViewGUID,
      });
    },
    fireReadMoreInteraction() {
      const ctx = this.getActiveStoryTrackingContext();
      if (!ctx.storyId) return;
      const payload = {
        interactionType: INTERACTION_TYPE.READ_MORE,
        storyId: ctx.storyId,
        storyVersionId: ctx.storyVersionId,
        sectionId: ctx.sectionId,
        regionId: ctx.regionId,
        regionTrackingId: ctx.regionTrackingId,
        previewUniqueViewGUID: this.previewTracking.currentGUID,
      };

      pushStoryInteraction(payload);
    },
  },
};
